import type { User } from '@/lib/types';

// ============================================================================
// CrewAhead Maritime Platform - Formatting Utilities
// ============================================================================

// ─── Date Formatting ─────────────────────────────────────────────────────────

/**
 * Predefined date format patterns for consistent formatting across the app.
 */
export const DATE_FORMATS = {
  /** Short date: Jan 15, 2024 */
  SHORT: 'short',
  /** Medium date: January 15, 2024 */
  MEDIUM: 'medium',
  /** Long date: Monday, January 15, 2024 */
  LONG: 'long',
  /** ISO date: 2024-01-15 */
  ISO: 'iso',
  /** Compact: 01/15/2024 */
  COMPACT: 'compact',
  /** Compact UK: 15/01/2024 */
  COMPACT_UK: 'compact_uk',
  /** Month Year: January 2024 */
  MONTH_YEAR: 'month_year',
  /** Date Time: Jan 15, 2024 2:30 PM */
  DATE_TIME: 'date_time',
} as const;

export type DateFormat = (typeof DATE_FORMATS)[keyof typeof DATE_FORMATS];

const LOCALE = 'en-GB';

/**
 * Formats a date value into a human-readable string.
 *
 * @param date - Date string, Date object, or timestamp
 * @param format - Format pattern to use
 * @returns Formatted date string, or empty string if date is invalid/null
 */
export function formatDate(
  date: string | Date | number | null | undefined,
  format: DateFormat = 'short'
): string {
  if (!date) return '';

  const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;

  if (isNaN(d.getTime())) return '';

  const formatters: Record<DateFormat, string> = {
    short: d.toLocaleDateString(LOCALE, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }),
    medium: d.toLocaleDateString(LOCALE, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
    long: d.toLocaleDateString(LOCALE, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
    iso: d.toISOString().split('T')[0],
    compact: d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }),
    compact_uk: d.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }),
    month_year: d.toLocaleDateString(LOCALE, {
      year: 'numeric',
      month: 'long',
    }),
    date_time: d.toLocaleDateString(LOCALE, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }),
  };

  return formatters[format] ?? formatters.short;
}

/**
 * Returns a relative time description (e.g., "2 days ago", "in 3 hours").
 *
 * @param date - Date string, Date object, or timestamp
 * @returns Relative time string, or empty string if date is invalid
 */
export function formatRelative(date: string | Date | number | null | undefined): string {
  if (!date) return '';

  const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;

  if (isNaN(d.getTime())) return '';

  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSec = Math.round(diffMs / 1000);
  const diffMin = Math.round(diffSec / 60);
  const diffHour = Math.round(diffMin / 60);
  const diffDay = Math.round(diffHour / 24);
  const diffWeek = Math.round(diffDay / 7);
  const diffMonth = Math.round(diffDay / 30);
  const diffYear = Math.round(diffDay / 365);

  const rtf = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' });

  if (Math.abs(diffSec) < 60) return rtf.format(-diffSec, 'second');
  if (Math.abs(diffMin) < 60) return rtf.format(-diffMin, 'minute');
  if (Math.abs(diffHour) < 24) return rtf.format(-diffHour, 'hour');
  if (Math.abs(diffDay) < 7) return rtf.format(-diffDay, 'day');
  if (Math.abs(diffWeek) < 4) return rtf.format(-diffWeek, 'week');
  if (Math.abs(diffMonth) < 12) return rtf.format(-diffMonth, 'month');
  return rtf.format(-diffYear, 'year');
}

// ─── Currency Formatting ─────────────────────────────────────────────────────

export interface SalaryFormatOptions {
  currency?: string;
  period?: 'month' | 'day' | 'year' | 'trip';
  compact?: boolean;
  showPeriod?: boolean;
}

/**
 * Formats a salary amount with currency symbol and optional period.
 *
 * @param amount - Salary amount
 * @param options - Formatting options
 * @returns Formatted salary string (e.g., "$5,000 / month")
 */
export function formatSalary(
  amount: number | null | undefined,
  options: SalaryFormatOptions = {}
): string {
  const { currency = 'USD', period = 'month', compact = false, showPeriod = true } = options;

  if (amount === null || amount === undefined || isNaN(amount)) return 'Negotiable';

  if (amount === 0 && !showPeriod) return 'Negotiable';

  const formatter = new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency,
    minimumFractionDigits: compact ? 0 : 2,
    maximumFractionDigits: compact ? 0 : 2,
  });

  const formatted = formatter.format(amount);
  const periodLabel =
    {
      month: '/ month',
      day: '/ day',
      year: '/ year',
      trip: '/ trip',
    }[period] ?? '';

  return showPeriod ? `${formatted} ${periodLabel}`.trim() : formatted;
}

// ─── Number Formatting ───────────────────────────────────────────────────────

/**
 * Formats a number with locale-appropriate separators.
 *
 * @param value - Number to format
 * @param decimals - Number of decimal places
 * @returns Formatted number string
 */
export function formatNumber(
  value: number | null | undefined,
  decimals = 0
): string {
  if (value === null || value === undefined || isNaN(value)) return '-';

  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Formats a number with a compact notation (e.g., 1.2K, 3.5M).
 *
 * @param value - Number to format
 * @returns Compact number string
 */
export function formatCompactNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) return '-';

  return new Intl.NumberFormat(LOCALE, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

// ─── Duration Formatting ─────────────────────────────────────────────────────

/**
 * Formats a duration in months into a human-readable string.
 *
 * @param months - Duration in months
 * @returns Formatted duration (e.g., "2 years 3 months", "6 months")
 */
export function formatDuration(months: number | null | undefined): string {
  if (months === null || months === undefined || months < 0) return '-';

  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  const parts: string[] = [];
  if (years > 0) parts.push(`${years} ${years === 1 ? 'year' : 'years'}`);
  if (remainingMonths > 0) parts.push(`${remainingMonths} ${remainingMonths === 1 ? 'month' : 'months'}`);

  return parts.length > 0 ? parts.join(' ') : '< 1 month';
}

/**
 * Formats total sea time (months) into a professional maritime format.
 *
 * @param totalMonths - Total sea time in months
 * @returns Formatted sea time string (e.g., "5 years 2 months (62 months)")
 */
export function formatSeaTime(totalMonths: number | null | undefined): string {
  if (totalMonths === null || totalMonths === undefined || totalMonths < 0) {
    return 'No sea time recorded';
  }

  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  if (years === 0) return `${months} ${months === 1 ? 'month' : 'months'}`;

  const parts: string[] = [];
  parts.push(`${years} ${years === 1 ? 'year' : 'years'}`);
  if (months > 0) parts.push(`${months} ${months === 1 ? 'month' : 'months'}`);

  return `${parts.join(' ')} (${totalMonths} months)`;
}

// ─── File Size Formatting ────────────────────────────────────────────────────

/**
 * Formats bytes into human-readable file size.
 *
 * @param bytes - File size in bytes
 * @param decimals - Decimal places
 * @returns Formatted file size (e.g., "2.5 MB")
 */
export function formatFileSize(bytes: number | null | undefined, decimals = 2): string {
  if (bytes === null || bytes === undefined || bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
}

// ─── String Formatting ───────────────────────────────────────────────────────

/**
 * Converts a string to Title Case.
 *
 * @param text - The string to convert
 * @returns Title-cased string (e.g., "chief_officer" → "Chief Officer")
 *
 * @example
 * ```ts
 * toTitleCase('hello world');    // "Hello World"
 * toTitleCase('chief_officer');  // "Chief Officer"
 * toTitleCase('STCW_BASIC');     // "Stcw Basic"
 * ```
 */
export function toTitleCase(text: string | null | undefined): string {
  if (!text) return '';

  return text
    .replace(/[_-]+/g, ' ') // Replace underscores and hyphens with spaces
    .replace(/\s+/g, ' ') // Collapse multiple spaces
    .toLowerCase()
    .split(' ')
    .map((word) => (word.length > 0 ? word[0].toUpperCase() + word.slice(1) : ''))
    .join(' ');
}

/**
 * Converts a string to kebab-case.
 *
 * @param text - The string to convert
 * @returns Kebab-cased string (e.g., "Chief Officer" → "chief-officer")
 */
export function toKebabCase(text: string | null | undefined): string {
  if (!text) return '';

  return text
    .replace(/([a-z])([A-Z])/g, '$1-$2') // camelCase → camel-Case
    .replace(/[\s_]+/g, '-') // Spaces/underscores → hyphens
    .toLowerCase()
    .replace(/^-+|-+$/g, ''); // Trim leading/trailing hyphens
}

/**
 * Converts a string to snake_case.
 *
 * @param text - The string to convert
 * @returns Snake-cased string (e.g., "Chief Officer" → "chief_officer")
 */
export function toSnakeCase(text: string | null | undefined): string {
  if (!text) return '';

  return text
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase()
    .replace(/^_+|_+$/g, '');
}

/**
 * Truncates text to a maximum length with an ellipsis.
 *
 * @param text - The string to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated string with ellipsis if needed
 */
export function truncateText(text: string | null | undefined, maxLength: number): string {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

// ─── Location Derivation ─────────────────────────────────────────────────────

/**
 * Derives a human-readable current location string from the user object.
 * Prefers the taxonomy-backed cityCountry label, then falls back to the
 * free-text city, joined with countryOfResidence.
 */
export function deriveLocation(user: User | null): string | null {
  if (!user) return null;
  const city = user.cityCountry?.cityName ?? user.city;
  const location = [city, user.countryOfResidence].filter(Boolean).join(', ');
  return location || null;
}

// ─── Phone Formatting ────────────────────────────────────────────────────────

/**
 * Formats a phone number into E.164-like format.
 *
 * @param phone - Raw phone string
 * @returns Formatted phone number
 */
export function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '';

  const cleaned = phone.replace(/\D/g, '');

  if (cleaned.length === 10) {
    return `+1 (${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  if (cleaned.length > 10) {
    const countryCode = cleaned.slice(0, cleaned.length - 10);
    const rest = cleaned.slice(-10);
    return `+${countryCode} (${rest.slice(0, 3)}) ${rest.slice(3, 6)}-${rest.slice(6)}`;
  }

  return phone;
}
